import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border border-graphite/20 px-4 py-3.5 text-sm text-graphite placeholder:text-graphite-soft/50 focus:outline-none focus:border-forest transition-colors duration-200";

export const SubmissionForm = ({ formType, fields, submitLabel, testid }) => {
  const { t, lang } = useLanguage();
  const [values, setValues] = useState({});
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);

  const set = (k, v) => setValues((p) => ({ ...p, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error(t("forms.consent"));
      return;
    }
    setSending(true);
    const details = {};
    fields.forEach((f) => {
      if (!["name", "email", "message"].includes(f.name) && values[f.name]) details[f.label] = values[f.name];
    });
    try {
      await axios.post(`${API}/forms/${formType}`, {
        name: values.name || "",
        email: values.email || "",
        message: values.message || "",
        consent,
        details,
        lang,
      });
      toast.success(t("forms.success"));
      setValues({});
      setConsent(false);
    } catch {
      toast.error(t("forms.error"));
    }
    setSending(false);
  };

  return (
    <form onSubmit={onSubmit} className="space-y-7" data-testid={`${testid}-form`} noValidate={false}>
      <div className="grid gap-7 md:grid-cols-2">
        {fields.map((f) => (
          <div key={f.name} className={f.type === "textarea" ? "md:col-span-2" : ""}>
            <label
              className="overline-mono mb-2.5 block text-graphite-soft"
              htmlFor={`${testid}-${f.name}`}
            >
              {f.label}
              {f.required !== false ? " *" : ""}
            </label>
            {f.type === "textarea" ? (
              <textarea
                id={`${testid}-${f.name}`}
                rows={5}
                required={f.required !== false}
                minLength={10}
                maxLength={5000}
                className={inputCls}
                value={values[f.name] || ""}
                onChange={(e) => set(f.name, e.target.value)}
                data-testid={`${testid}-field-${f.name}`}
              />
            ) : f.type === "select" ? (
              <select
                id={`${testid}-${f.name}`}
                required={f.required !== false}
                className={`${inputCls} appearance-none`}
                value={values[f.name] || ""}
                onChange={(e) => set(f.name, e.target.value)}
                data-testid={`${testid}-field-${f.name}`}
              >
                <option value="" disabled>
                  —
                </option>
                {f.options.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            ) : (
              <input
                id={`${testid}-${f.name}`}
                type={f.type || "text"}
                required={f.required !== false}
                minLength={f.name === "name" ? 2 : undefined}
                maxLength={500}
                className={inputCls}
                value={values[f.name] || ""}
                onChange={(e) => set(f.name, e.target.value)}
                data-testid={`${testid}-field-${f.name}`}
              />
            )}
          </div>
        ))}
      </div>
      <label className="flex cursor-pointer items-start gap-3 text-sm text-graphite-soft">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer appearance-none border border-graphite/30 checked:border-forest checked:bg-forest transition-colors duration-200"
          data-testid={`${testid}-consent-checkbox`}
        />
        <span>
          {t("forms.consent")}{" "}
          <Link to="/confidentialite" className="underline underline-offset-4 hover:text-forest transition-colors duration-200">
            {t("footer.privacy")}
          </Link>
        </span>
      </label>
      <button
        type="submit"
        disabled={sending}
        className="group inline-flex items-center gap-3 bg-forest px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-graphite disabled:opacity-50"
        data-testid={`${testid}-submit-button`}
      >
        {sending ? t("forms.sending") : submitLabel}
        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
      </button>
    </form>
  );
};

export const NewsletterForm = ({ context = "footer", dark = true }) => {
  const { t, lang } = useLanguage();
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error(t("forms.consent"));
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/newsletter`, { email, consent, lang });
      toast.success(t("forms.newsletterSuccess"));
      setEmail("");
      setConsent(false);
    } catch {
      toast.error(t("forms.error"));
    }
    setSending(false);
  };

  const cls = dark
    ? "border-white/25 text-white placeholder:text-white/40 focus:border-white"
    : "border-graphite/25 text-graphite placeholder:text-graphite-soft/50 focus:border-forest";

  return (
    <form onSubmit={onSubmit} className="space-y-4" data-testid={`${context}-newsletter-form`}>
      <div className="flex flex-col gap-3 sm:flex-row">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t("footer.newsletterPlaceholder")}
          className={`w-full border bg-transparent px-4 py-3.5 text-sm focus:outline-none transition-colors duration-200 ${cls}`}
          data-testid={`${context}-newsletter-email-input`}
        />
        <button
          type="submit"
          disabled={sending}
          className={`shrink-0 px-6 py-3.5 font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-300 disabled:opacity-50 ${
            dark ? "bg-white text-graphite hover:bg-forest hover:text-white" : "bg-forest text-white hover:bg-graphite"
          }`}
          data-testid={`${context}-newsletter-submit-button`}
        >
          {sending ? t("forms.sending") : t("footer.newsletterButton")}
        </button>
      </div>
      <label className={`flex cursor-pointer items-start gap-2.5 text-xs ${dark ? "text-white/50" : "text-graphite-soft"}`}>
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 h-3.5 w-3.5 shrink-0 cursor-pointer appearance-none border border-current checked:bg-forest checked:border-forest transition-colors duration-200"
          data-testid={`${context}-newsletter-consent-checkbox`}
        />
        <span>{t("forms.consent")}</span>
      </label>
    </form>
  );
};
